import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  ROTATION_OPTIONS,
  ROTATION_NOT_ROTATED,
  ROTATION_LEFT,
  ROTATION_RIGHT,
  VibeIcon,
  VibeTooltip,
  viExpandMore,
  viInformation,
  color,
} from 'vibeguide';
import Popper from '@mui/material/Popper';
import ScreenRotation from '@mui/icons-material/ScreenRotation';
import './RotationDropdown.scss';

/**
 * Render the icon for a given rotation value. Returns null when the option
 * has no associated icon (Not Rotated / Upside Down).
 */
function renderRotationIcon(value, size = 18) {
  if (value === ROTATION_LEFT) {
    return (
      <ScreenRotation
        style={{
          fontSize: size,
        }}
      />
    );
  }

  if (value === ROTATION_RIGHT) {
    return (
      <ScreenRotation
        style={{
          fontSize: size,
          transform: 'scaleX(-1)',
        }}
      />
    );
  }

  return null;
}

function RotationDropdown({
  name,
  label,
  value = ROTATION_NOT_ROTATED,
  required = false,
  disabled = false,
  labelTooltip = '',
  testId,
  tabIndex = 0,
  onChange = () => {},
}) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(() => {
    const idx = ROTATION_OPTIONS.findIndex(option => option.value === value);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    const idx = ROTATION_OPTIONS.findIndex(option => option.value === value);
    if (idx >= 0) {
      setHighlightIndex(idx);
    }
  }, [value]);

  const selected = ROTATION_OPTIONS.find(option => option.value === value)
    || ROTATION_OPTIONS[0];

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setOpen(prev => !prev);
  };

  const handleSelect = (option) => {
    onChange({
      name,
      value: option.value,
    });
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      handleSelect(ROTATION_OPTIONS[highlightIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (!open) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex(prev => (prev + 1) % ROTATION_OPTIONS.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex(prev => (
        (prev - 1 + ROTATION_OPTIONS.length) % ROTATION_OPTIONS.length
      ));
    }
  };

  return (
    <div
      ref={containerRef}
      className={classNames('RotationDropdown', 'Field', { disabled })}
    >
      {label && (
        <VibeTooltip
          title={labelTooltip}
          placement="right"
        >
          <label
            className={classNames('field-label', { required })}
            htmlFor={`${name}-rotation-trigger`}
          >
            {label}

            {labelTooltip && (
              <VibeIcon
                style={{
                  marginLeft: 4,
                }}
                icon={viInformation}
                color={color.manatee}
                size={12}
              />
            )}
          </label>
        </VibeTooltip>
      )}

      <div
        ref={triggerRef}
        id={`${name}-rotation-trigger`}
        className="rotation-trigger"
        role="button"
        tabIndex={disabled ? -1 : tabIndex}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <div className="rotation-trigger-content">
          {renderRotationIcon(selected.value, 18)}
          <span className="rotation-trigger-label">{selected.label}</span>
        </div>

        <VibeIcon
          icon={viExpandMore}
          color={color.manatee}
          size={20}
          className={classNames('rotation-trigger-caret', { open })}
        />
      </div>

      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        className="RotationDropdownPopper"
        style={{
          width: triggerRef.current ? triggerRef.current.offsetWidth : undefined,
          zIndex: 1500,
        }}
      >
        <ul
          className="rotation-options"
          role="listbox"
        >
          {ROTATION_OPTIONS.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={classNames('rotation-option', {
                selected: option.value === value,
                highlighted: index === highlightIndex,
              })}
              data-testid={testId ? `${testId}-option-${option.value}` : undefined}
              onMouseEnter={() => setHighlightIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelect(option);
              }}
            >
              <span className="rotation-option-label">{option.label}</span>
              {renderRotationIcon(option.value, 18)}
            </li>
          ))}
        </ul>
      </Popper>
    </div>
  );
}

RotationDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  labelTooltip: PropTypes.string,
  testId: PropTypes.string,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
};

export default RotationDropdown;
